/**
 * Anuncio — região `aria-live` única da aplicação (A11Y-04).
 *
 * O PROBLEMA
 *   O projeto inteiro não tinha **nenhuma** região `aria-live`. Quem usa leitor
 *   de tela clicava em "Salvar" e não era avisado de nada: nem que salvou, nem
 *   que deu erro, nem que apareceu um aviso. O retorno visual existia; o
 *   auditivo, não. Na prática, o trabalho de rotina não se completava só com
 *   leitor de tela.
 *
 * COMO USAR
 *   Monte `<Anuncio />` uma vez, no shell da aplicação. Em qualquer lugar:
 *
 *       import { anunciar } from './components/shared/Anuncio';
 *       anunciar('Cliente salvo.');
 *       anunciar('Não foi possível salvar.', 'assertivo');
 *
 * DUAS REGIÕES, NÃO UMA
 *   `polite` espera a leitura atual terminar — serve para confirmação.
 *   `assertive` interrompe — serve para erro, que a pessoa precisa ouvir agora.
 *   Usar `assertive` para tudo faz o leitor cortar a própria frase o tempo todo
 *   e vira ruído; a distinção existe para ser respeitada.
 */
import React, { useEffect, useState } from 'react';

const ouvintes = new Set();

/**
 * Anuncia uma mensagem para leitores de tela.
 *
 * @param {string} mensagem  Texto curto, em português comum.
 * @param {'educado'|'assertivo'} tom  'assertivo' interrompe a leitura atual.
 */
export function anunciar(mensagem, tom = 'educado') {
  if (!mensagem) return;
  ouvintes.forEach((ouvinte) => ouvinte({ mensagem, tom, id: Date.now() + Math.random() }));
}

export default function Anuncio() {
  const [educado, setEducado] = useState('');
  const [assertivo, setAssertivo] = useState('');

  useEffect(() => {
    const ouvinte = ({ mensagem, tom }) => {
      const definir = tom === 'assertivo' ? setAssertivo : setEducado;
      // Limpa antes de escrever: se o texto novo for IGUAL ao anterior, o
      // leitor de tela entende que nada mudou e fica calado. Salvar duas vezes
      // seguidas anunciaria só a primeira.
      definir('');
      window.setTimeout(() => definir(mensagem), 50);
    };
    ouvintes.add(ouvinte);
    return () => { ouvintes.delete(ouvinte); };
  }, []);

  return (
    <>
      <div
        className="apenas-leitor-de-tela"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {educado}
      </div>
      <div
        className="apenas-leitor-de-tela"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {assertivo}
      </div>
    </>
  );
}
